import { DateValue } from '@givebutter/primitives/date-picker';
import { FetchTasksParams } from 'api/tasks';
import { DueAtPreset } from 'components/filter/filters-next/filters/DueAt';
import { TASK_LINK_DATA_ATTRIBUTE } from 'components/tasks/constants';
import { TaskFormValues } from 'components/tasks/utils/useTaskForm';
import { TaskFilterStoreValues } from 'components/tasks/utils/useTasksFilterStore';
import { dateToServerDateTime } from 'helpers/dates';
import { getNodesFromHtml } from 'helpers/dom';
import { DateTime } from 'luxon';

const taskFormToRequest = (formValues: TaskFormValues) => {
    const { status, due_at, remind_at, links, description, assigned_to, ...rest } = formValues;

    return {
        ...rest,
        status: status,
        due_at: due_at ? dateToServerDateTime(due_at.endOf('day')) : null,
        assigned_to: assigned_to[0] ? parseInt(assigned_to[0]) : null,
        remind_at: remind_at ? dateToServerDateTime(remind_at) : null,
        links: links.map(link => {
            const { value, ...rest } = link;
            return rest;
        }),
        description: dehydrateDescription(description),
    };
};

const taskFiltersToRequest = (formValues: TaskFilterStoreValues): FetchTasksParams => {
    return {
        advancedQuery: formValues.query || undefined,
        sortByDesc: formValues.sortByDesc ?? undefined,
        sortBy: formValues.sortBy ?? undefined,
    };
};

const transformDueAtPresetToValue = (preset: Exclude<DueAtPreset, 'custom'>): DateValue | null => {
    switch (preset) {
        case 'today':
            return DateTime.now();
        case 'tomorrow':
            return DateTime.now().plus({ days: 1 });
        case 'overdue':
            return DateTime.now().minus({ days: 1 });
        case 'this_week':
            return [DateTime.now().startOf('week'), DateTime.now().endOf('week')];
        case 'next_week':
            return [
                DateTime.now().plus({ weeks: 1 }).startOf('week'),
                DateTime.now().plus({ weeks: 1 }).endOf('week'),
            ];
        case 'this_month':
            return [DateTime.now().startOf('month'), DateTime.now().endOf('month')];
        case 'next_month':
            return [
                DateTime.now().plus({ months: 1 }).startOf('month'),
                DateTime.now().plus({ months: 1 }).endOf('month'),
            ];
        case 'no_due_date':
        default:
            return null;
    }
};

const dehydrateDescription = (
    description: TaskFormValues['description'],
): TaskFormValues['description'] => {
    const body = getNodesFromHtml({ html: description, selectors: 'body' })[0];
    const descriptionLinks = body.querySelectorAll(`span[${TASK_LINK_DATA_ATTRIBUTE}]`);

    for (const link of Array.from(descriptionLinks)) {
        link.innerHTML = '';
        link.removeAttribute('class');
    }

    return body.innerHTML;
};

export { taskFiltersToRequest, taskFormToRequest, transformDueAtPresetToValue };
